import type { OrbitaModule } from '@/shared/registry/types';

import { isOtherEditionPath } from './edition';

interface ModuleEntry {
  module?: OrbitaModule;
}

/**
 * Discovers every module at build time: each `src/modules/<id>/index.ts` exports `module`.
 * Adding a module requires no change outside its folder (ARCHITECTURE §6.2).
 *
 * The modules of an edition (docs/EDITIONS.md) are `src/editions/<edition>/modules/<id>/index.ts`; only the edition this
 * build is made for contributes its modules, and the base product has no such folder.
 */
export function discoverModules(): OrbitaModule[] {
  const entries = import.meta.glob<ModuleEntry>(
    ['../modules/*/index.ts', '../editions/*/modules/*/index.ts'],
    {
      eager: true,
    },
  );
  const modules: OrbitaModule[] = [];
  for (const [path, entry] of Object.entries(entries)) {
    if (isOtherEditionPath(path)) continue;
    if (!entry.module) throw new Error(`${path} must export \`module\` (see docs/ARCHITECTURE.md §6.2).`);
    const folder = /modules\/([^/]+)\/index\.ts$/u.exec(path)?.[1];
    if (folder !== entry.module.id) {
      throw new Error(`Module id "${entry.module.id}" must match its folder name "${folder ?? path}".`);
    }
    modules.push(entry.module);
  }
  return modules;
}
